"use client";

import React, { useState, useCallback } from "react";
import {
  SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, Filter, LogIn, LogOut } from "lucide-react";
import {
  ButtonPrimary,
  ButtonOutline,
  ButtonIcon,
  ButtonTable,
  ButtonSecondary,
  ButtonGhost,
  ButtonDestructive,
  ButtonSuccess,
} from "./StandardButton";
import RowSelectorButton from "./RowSelectorButton";
import { DataViewProps, normalizePagination } from "./DataViewTypes";
import EmptyState from "@/components/ui/empty-state";
import { cn } from "@/lib/utils";

export function DataView<TData, TValue>({
  columns,
  data,
  pagination,
  pageIndex,
  onPageChange,
  setPageIndex, // For backward compatibility
  loading,
  error,
  title,
  description,
  searchPlaceholder = "Search...",
  searchQuery = "",
  onSearch,
  filterConfig,
  actionButtons = [],
  exportFunc,
  importFunc,
  showExportImport = false,
  onRowClick,
  enableRowSelection = false,
  rowSelection: controlledRowSelection,
  onRowSelectionChange,
  enableColumnResizing = false,
  emptyMessage = "No results found.",
  emptyStateType,
  // loadingMessage = "Loading...", // Currently unused
  cardWrapper = false,
  cardWrapperClassName = "",
  className = "",
  meta,
}: DataViewProps<TData, TValue>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [internalRowSelection, setInternalRowSelection] = useState({});

  // Use controlled selection if provided, otherwise use internal state
  const rowSelection = controlledRowSelection ?? internalRowSelection;

  // Handle row selection changes - support both direct values and updater functions
  const handleRowSelectionChange = useCallback(
    (updaterOrValue: any) => {
      const newValue =
        typeof updaterOrValue === "function"
          ? updaterOrValue(rowSelection)
          : updaterOrValue;

      if (onRowSelectionChange) {
        onRowSelectionChange(newValue);
      } else {
        setInternalRowSelection(newValue);
      }
    },
    [rowSelection, onRowSelectionChange],
  );
  const [localSearchQuery, setLocalSearchQuery] = useState(searchQuery);

  // Normalize pagination format
  const normalizedPagination = normalizePagination(pagination);
  const totalPages = normalizedPagination.totalPages;
  const pageSize = normalizedPagination.pageSize;
  const totalEntries = normalizedPagination.total;

  // Create unified page change handler
  const handlePageChange = (newPageIndex: number) => {
    if (onPageChange) {
      onPageChange(newPageIndex);
    } else if (setPageIndex) {
      setPageIndex(newPageIndex);
    }
  };

  const maxVisiblePages = 5;
  const half = Math.floor(maxVisiblePages / 2);

  const getVisiblePageNumbers = useCallback(
    (pageIndex: number): number[] => {
      let start = Math.max(0, pageIndex - half);
      let end = Math.min(totalPages - 1, pageIndex + half);

      if (end - start + 1 < maxVisiblePages) {
        if (start === 0) {
          end = Math.min(start + maxVisiblePages - 1, totalPages - 1);
        } else if (end === totalPages - 1) {
          start = Math.max(0, end - maxVisiblePages + 1);
        }
      }

      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },
    [half, totalPages],
  );

  const visiblePageNumbers = getVisiblePageNumbers(pageIndex);
  const safeData = Array.isArray(data) ? data : [];

  const table = useReactTable({
    data: safeData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: handleRowSelectionChange,
    enableColumnResizing: enableColumnResizing,
    columnResizeMode: enableColumnResizing ? "onChange" : undefined,
    pageCount: totalPages,
    state: {
      sorting,
      rowSelection,
    },
    manualPagination: true,
    meta,
  });

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(localSearchQuery);
    }
  };

  // Helper function to get error message
  const getErrorMessage = () => {
    if (!error) return null;
    if (typeof error === "string") return error;
    if (typeof error === "object" && error.message) return error.message;
    return "An error occurred";
  };

  const containerClassName = cardWrapper
    ? cn(
        "bg-card p-[22px] border rounded-[20px] shadow-lg space-y-3 flex flex-col h-full",
        cardWrapperClassName,
      )
    : `space-y-4 ${className}`;

  return (
    <div className={containerClassName}>
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 flex-shrink-0">
        <div className="flex-1">
          <h2 className="text-xl font-bold text-foreground mb-1">{title}</h2>
          {description && (
            <p className="text-xs text-muted-foreground">{description}</p>
          )}
          {!!totalEntries && (
            <p className="text-xs text-muted-foreground mt-1">
              {totalEntries} {totalEntries === 1 ? "entry" : "entries"} found
            </p>
          )}
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Export/Import Buttons */}
          {showExportImport && (exportFunc || importFunc) && (
            <div className="flex items-center gap-2">
              {exportFunc && (
                <ButtonPrimary
                  size="sm"
                  onClick={exportFunc}
                  className="h-8 px-3 text-xs"
                >
                  <LogOut className="rotate-270 w-4 h-4 mr-2" />
                  Export
                </ButtonPrimary>
              )}
              {importFunc && (
                <ButtonPrimary
                  size="sm"
                  onClick={importFunc}
                  className="h-8 px-3 text-xs"
                >
                  <LogIn className="rotate-90 w-4 h-4 mr-2" />
                  Import
                </ButtonPrimary>
              )}
            </div>
          )}

          {/* Row Selector Button */}
          {enableRowSelection && <RowSelectorButton table={table} />}

          {/* Search Bar */}
          {onSearch && (
            <form onSubmit={handleSearchSubmit} className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={localSearchQuery}
                  onChange={(e) => setLocalSearchQuery(e.target.value)}
                  className="pl-8 pr-4 w-60 h-8 text-xs bg-table-background border-border focus:ring-2 focus:ring-primary focus:border-primary rounded-full shadow-sm focus:shadow-primary/20"
                />
              </div>
            </form>
          )}

          {/* Filter Button */}
          {filterConfig && (
            <ButtonOutline
              size="sm"
              onClick={filterConfig.onToggle}
              className={`relative h-8 px-3 text-xs transition-all duration-200 shadow-sm hover:shadow-md active:shadow-none ${
                filterConfig.isOpen
                  ? "bg-primary text-primary-foreground border-primary"
                  : ""
              }`}
            >
              <Filter className="h-3 w-3 mr-2" />
              Filters
              {(filterConfig.activeCount ?? 0) > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                  {filterConfig.activeCount}
                </span>
              )}
            </ButtonOutline>
          )}

          {/* Action Buttons */}
          {actionButtons.map((button, index) => {
            const ButtonComponent = (() => {
              switch (button.variant) {
                case "primary":
                  return ButtonPrimary;
                case "secondary":
                  return ButtonSecondary;
                case "ghost":
                  return ButtonGhost;
                case "destructive":
                  return ButtonDestructive;
                case "success":
                  return ButtonSuccess;
                case "outline":
                default:
                  return ButtonOutline;
              }
            })();

            return (
              <ButtonComponent
                key={index}
                size="sm"
                onClick={button.onClick}
                disabled={button.disabled}
                className={`h-8 px-3 text-xs transition-all duration-200 ${button.className || ""}`}
              >
                {button.icon && <span className="mr-2">{button.icon}</span>}
                {button.label}
              </ButtonComponent>
            );
          })}
        </div>
      </div>

      {/* Filter Panel */}
      {filterConfig?.isOpen && filterConfig.component}

      {/* Table Section */}
      <div className="rounded-md border-none overflow-auto scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400 relative flex-1 min-h-0">
        <Table
          className="border-none border-separate overflow-hidden flex-1"
          style={{
            borderSpacing: "0 6px",
            tableLayout: enableColumnResizing ? "auto" : undefined,
            width: enableColumnResizing
              ? `${table.getCenterTotalSize()}px`
              : undefined,
            minWidth: "100%",
          }}
        >
          <TableHeader className="border-b-[2px] h-10 border-b-table-background rounded-t-[6px] overflow-hidden">
            {loading ? (
              <TableRow className="bg-background border-b border-border/50 w-fit p-0 m-0">
                <TableHead className="first:rounded-l-[10px] last:rounded-r-[10px] p-0 m-0">
                  <div className="h-[40px] w-full bg-muted rounded animate-pulse" />
                </TableHead>
              </TableRow>
            ) : (
              table.getHeaderGroups().map((headerGroup) => (
                <TableRow className="border-0 group" key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => {
                    return (
                      <TableHead
                        key={header.id}
                        style={
                          enableColumnResizing
                            ? {
                                width: `${header.column.getSize()}px`,
                                minWidth: `${header.column.columnDef.minSize || header.column.getSize()}px`,
                                maxWidth: `${header.column.columnDef.maxSize || "none"}`,
                              }
                            : undefined
                        }
                        className={`
                          py-2 px-3 border-0 text-xs
                          bg-background
                          font-bold text-foreground
                          ${index === 0 ? "rounded-l-[6px]" : ""}
                          ${index === headerGroup.headers.length - 1 ? "rounded-r-[6px]" : ""}
                        `
                          .replace(/\s+/g, " ")
                          .trim()}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableHeader>
          <TableBody>
            {loading ? (
              Array(6)
                .fill(0)
                .map((_, index) => (
                  <TableRow
                    className="bg-background border-b border-border/50 p-0 m-0"
                    key={index}
                  >
                    <TableCell className="first:rounded-l-[10px] last:rounded-r-[10px] p-0 m-0">
                      <div className="h-[40px] p-0 m-0 w-full bg-muted rounded animate-pulse" />
                    </TableCell>
                  </TableRow>
                ))
            ) : error ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-20 text-center text-xs bg-white/50 dark:bg-background rounded-[6px] border-0"
                >
                  {getErrorMessage()}
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows?.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  className={`group transition-colors ${onRowClick ? "cursor-pointer hover:bg-blue-50/80 dark:hover:bg-primary/30" : ""}`}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <TableCell
                      key={cell.id}
                      style={
                        enableColumnResizing
                          ? {
                              width: `${cell.column.getSize()}px`,
                              minWidth: `${cell.column.columnDef.minSize || cell.column.getSize()}px`,
                              maxWidth: `${cell.column.columnDef.maxSize || "none"}`,
                            }
                          : undefined
                      }
                      className={`
                        py-2 px-3 border-0 text-xs
                        bg-background
                        group-hover:bg-blue-50/60 dark:group-hover:bg-primary/20
                        group-data-[state=selected]:bg-blue-50/80 dark:group-data-[state=selected]:bg-primary/20
                        ${index === 0 ? "rounded-l-[6px]" : ""}
                        ${index === row.getVisibleCells().length - 1 ? "rounded-r-[6px]" : ""}
                      `
                        .replace(/\s+/g, " ")
                        .trim()}
                    >
                      {(() => {
                        const rendered = flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        );

                        // Allow valid React elements and primitives to pass through
                        if (
                          rendered === null ||
                          rendered === undefined ||
                          typeof rendered === "string" ||
                          typeof rendered === "number" ||
                          typeof rendered === "boolean" ||
                          Array.isArray(rendered) ||
                          React.isValidElement(rendered)
                        ) {
                          return rendered;
                        }

                        // If a plain object was returned accidentally, stringify it
                        if (typeof rendered === "object") {
                          try {
                            return JSON.stringify(rendered);
                          } catch {
                            return String(rendered);
                          }
                        }

                        // Fallback
                        return rendered as React.ReactNode;
                      })()}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="p-0 border-0">
                  <EmptyState
                    type={emptyStateType}
                    title={emptyStateType ? undefined : emptyMessage}
                    size="sm"
                    className="py-8"
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination Section */}
      <div className="flex items-center justify-between flex-shrink-0">
        <p className="text-xs text-muted-foreground">
          {!!totalEntries && totalPages > 1
            ? `Showing ${pageIndex * pageSize + 1} to
              ${Math.min((pageIndex + 1) * pageSize, totalEntries)} of ${totalEntries}
              entries`
            : ""}
        </p>
        <div className="flex items-center justify-end space-x-2">
          <ButtonTable
            onClick={() => handlePageChange(pageIndex - 1)}
            disabled={pageIndex === 0}
            className="text-xs h-7 px-3 hover:opacity-50"
          >
            Previous
          </ButtonTable>
          {/* Page Numbers */}
          {visiblePageNumbers.map((pageNum) => (
            <ButtonIcon
              key={pageNum}
              onClick={() => handlePageChange(pageNum)}
              className={`w-7 h-7 text-xs hover:opacity-50 ${
                pageIndex === pageNum ? "!bg-primary !text-white" : ""
              }`}
            >
              {pageNum + 1}
            </ButtonIcon>
          ))}
          <ButtonTable
            onClick={() => handlePageChange(pageIndex + 1)}
            disabled={pageIndex >= totalPages - 1}
            className="text-xs h-7 px-3 hover:opacity-50"
          >
            Next
          </ButtonTable>
        </div>
      </div>
    </div>
  );
}

export default DataView;
